// External library imports
import { useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { Typography, CircularProgress, Button } from '@mui/material';

// Internal component imports
import DashboardShiftRow from '../../DashboardShiftRow/DashboardShiftRow';
import Spinner from '../../../assets/svg/spinner';
import { DownloadIcon } from '../../../assets/svg';
import { ShiftWeekNavigation } from '../../../components';
import AutoStaffButton from '../../AutoStaffButton/AutoStaffButton';
import { showErrorToast, showSuccessToast } from '../../../utils/toastUtils';

// Internal utility/hook imports
import { useProcessedShifts } from '../../../utils/Hooks/shifts/useProcessedShifts';
import { useVenueTimeFormatters } from '../../../utils/Hooks/venue/useVenueTimeFormatters';
import { currencyFormatter } from '../../../utils/helpers/formatting/currencyHelpers';
import { selectHasPrivilegedRole } from '../../../redux/slices/currentUserSlice';
import {
  selectEmployeeShifts,
  selectAllShifts,
  selectShiftStatus,
  selectPeriod,
} from '../../../redux/shifts/shiftsSlice';
import { selectDailyTickets } from '../../../redux/dailyTickets/dailyTicketsSlice';
import { LOADING_STATES } from '../../../utils/constants/loadingStates';
import { downloadShiftsPDF } from '../../../redux/shifts/shiftsThunks';

// Internal style imports
import { WeekTitle, NoShiftsText, ScheduleContainer, TitleContainer, ActionsContainer } from './WeeklySchedule.styles';
import { SpinnerContainer } from '../../../pages/Dashboard/Dashboard.styles';
import { DrawerContentContainer } from '../../AdminDrawer/AdminDrawer.styles';

/**
 * WeeklySchedule Component
 * @description Displays a week's worth of shifts in a daily schedule format.
 * Each day shows scheduled hours and shift status, with the ability to click for more details.
 * For admin users, it also displays labor cost percentages from daily tickets.
 *
 * @param {Object} props
 * @param {function} props.onDayClick - Callback when a day's shifts are clicked
 */
const WeeklySchedule = ({ onDayClick }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { formatDate, venueTimezone } = useVenueTimeFormatters();

  // Get data from Redux
  const hasPrivilegedRole = useSelector(selectHasPrivilegedRole);
  const shiftStatus = useSelector(selectShiftStatus);
  const userShiftsThisWeek = useSelector(selectEmployeeShifts);
  const allShiftsThisWeek = useSelector(selectAllShifts);
  const dailyTickets = useSelector(selectDailyTickets);
  const period = useSelector(selectPeriod);
  const startOfWeekTimestamp = dayjs(period?.start).valueOf();

  // Process shifts for display in the weekly view
  const weeklyDisplayShifts = hasPrivilegedRole ? allShiftsThisWeek : userShiftsThisWeek;
  const processedShifts = useProcessedShifts(weeklyDisplayShifts, startOfWeekTimestamp, shiftStatus);

  // Calculate the weekly revenue and labor cost from the daily tickets data
  const totalNetSales = dailyTickets.reduce((sum, ticket) => sum + (ticket.net || 0), 0);
  const totalLaborCost = dailyTickets.reduce((sum, ticket) => sum + (ticket.cost || 0), 0);

  // Generate report from current dailyTickets
  const shareReport = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadShiftsPDF(
        {
          period,
          offset: {
            now: new Date().toISOString(),
            tz: venueTimezone,
          },
          shifts: weeklyDisplayShifts.filter((_) => _.assignedName),
        },
        `Schedule - Week of ${formatDate(startOfWeekTimestamp)}.pdf`
      );
      showSuccessToast('Shift Schedule report was generated successfully');
    } catch (error) {
      showErrorToast(`Failed to generate Shift Schedule report (${error.message})`);
    } finally {
      setIsDownloading(false);
    }
  };

  // Show loading state if shifts are loading or if we don't have the period data yet
  if (shiftStatus === LOADING_STATES.LOADING || !period?.start || !period?.end) {
    return (
      <SpinnerContainer>
        <Spinner />
      </SpinnerContainer>
    );
  }

  return (
    <DrawerContentContainer>
      <TitleContainer>
        <WeekTitle variant="h4">
          <ShiftWeekNavigation noPadding renderTitle={() => `Week of ${formatDate(startOfWeekTimestamp)}`} />
        </WeekTitle>
        {hasPrivilegedRole && (
          <Typography variant="subtitle1">
            {currencyFormatter(totalNetSales)} net sales, {currencyFormatter(totalLaborCost)} labor cost
          </Typography>
        )}
      </TitleContainer>

      <ScheduleContainer>
        {weeklyDisplayShifts && weeklyDisplayShifts.length > 0 ? (
          processedShifts.map((shift) => {
            // Find the daily ticket for this day using the first shift's start time
            const shiftDate = dayjs(shift.shifts[0]?.start).format('YYYY-MM-DD');
            const dailyTicket = dailyTickets.find((ticket) => dayjs(ticket.date).format('YYYY-MM-DD') === shiftDate);

            // For day drilldown, get all shifts for that day
            const dayShifts = allShiftsThisWeek.filter(
              (s) => dayjs(s.start).format('YYYY-MM-DD') === dayjs(shift.shifts[0]?.start).format('YYYY-MM-DD')
            );

            return (
              <DashboardShiftRow
                key={`${shift.day}-${shift.dateNumber}`}
                day={shift.day}
                dateNumber={shift.dateNumber}
                isToday={shift.isToday}
                isPast={shift.isPast}
                scheduledHours={shift.scheduledHours}
                shiftStatus={shift.shiftStatus}
                shifts={shift.shifts}
                futureShiftsCount={shift.futureShiftsCount}
                onDayClick={() =>
                  onDayClick({
                    shifts: dayShifts,
                    selectedDay: dayjs(startOfWeekTimestamp)
                      .add(processedShifts.indexOf(shift), 'day')
                      .startOf('day')
                      .toISOString(),
                  })
                }
                isLast={shift === processedShifts[processedShifts.length - 1]}
                dailyTicket={dailyTicket}
              />
            );
          })
        ) : (
          <NoShiftsText variant="body1">No shifts scheduled for this week.</NoShiftsText>
        )}
      </ScheduleContainer>

      <ActionsContainer>
        <Button variant="primaryDark" onClick={shareReport} disabled={isDownloading}>
          {isDownloading ? (
            <Typography variant="subheadlineRegular">Exporting...</Typography>
          ) : (
            <Typography variant="subheadlineRegular">Export</Typography>
          )}
          {isDownloading ? <CircularProgress color="inherit" size="24px" /> : <DownloadIcon color="white" size="24" />}
        </Button>
        <AutoStaffButton periodHasShifts={weeklyDisplayShifts.length > 0} />
      </ActionsContainer>
    </DrawerContentContainer>
  );
};

WeeklySchedule.propTypes = {
  onDayClick: PropTypes.func.isRequired,
};

export default WeeklySchedule;
